import type { Hit } from 'instantsearch.js';

import React from 'react';
import icons from './Hit.Icons';
import { useIsMainSubscribed } from '@src/hooks/user';
import { useDocument } from '@src/hooks/document';
import { Link } from 'gatsby';
import { useResourceLink } from './hooks';
import CartButton from '@src/components/CartButton';
import { trackDocumentClick } from '@src/utils/trackDocumentClick';

type HitLinkProps = {
  page: boolean;
  hit: Hit;
};

const styles = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
};

const HitPage = ({ hit, page }: HitLinkProps) => {
  if (page) {
    return <a href={hit.slug}>Page</a>;
  }

  if (hit?.paths?.module) {
    return <a href={hit.paths.module}>Page</a>;
  }

  return null;
};

export const HitUserAction = ({ hit, page }: HitLinkProps) => {
  const isSubscribed = useIsMainSubscribed();
  const { handleSubscibeOrBuyClick } = useDocument(hit.requires_subscription, hit.id);

  if (page) {
    return null;
  }

  if (hit?.free_sample) {
    return (
      <span className="category-option-info lh-1.5">
        <span className="badge free-sample" style={{ textTransform: 'uppercase', marginRight: 0 }}>
          Free Sample
        </span>
      </span>
    );
  }

  // 1: (Basket Icon)
  if (hit?.paths?.detail && hit?.has_password && !hit?.requires_subscription) {
    return <CartButton id={hit.id} />;
  }

  // 2: Subscription - (Basket and Subscription Icon)
  if (hit?.requires_subscription && hit?.has_password) {
    return (
      <div style={styles}>
        {isSubscribed ? (
          <button
            onClick={() => {
              trackDocumentClick(hit, {
                source: 'search',
                outcome: 'opened',
                isSubscribed,
                destinationUrl: `/embeded?id=${hit.id}`,
              });
              handleSubscibeOrBuyClick(hit.id);
            }}
          >
            {icons.unlockedSub}
          </button>
        ) : (
          <>
            {icons.lockedSub}
            <Link
              className="button-b badge free-sample"
              to="/subscriptions"
              style={{ margin: 0, padding: '0 5px', color: 'white', textDecoration: 'none' }}
              onClick={() =>
                trackDocumentClick(hit, {
                  source: 'search',
                  outcome: 'subscribe',
                  isSubscribed,
                  destinationUrl: '/subscriptions',
                })
              }
            >
              Subscribe
            </Link>
            <CartButton id={hit.id} />
          </>
        )}
      </div>
    );
  }

  if (hit?.has_password && hit?.free_document) {
    return null;
  }

  // 4: Basket (Basket Icon)
  if (hit?.has_password) {
    return <CartButton id={hit.id} />;
  }

  return null;
};

const HitResource = ({ hit, page }: HitLinkProps) => {
  const resourceLink = useResourceLink(hit, page);
  if (page) {
    return null;
  }
  return (
    <button
      onClick={resourceLink?.onClick}
      style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
    >
      <span>Resource</span> {resourceLink?.icon}
    </button>
  );
};

const HitLink = (props: HitLinkProps) => {
  return (
    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
      <HitPage {...props} />
      <HitResource {...props} />
    </div>
  );
};

export default HitLink;
